import { useState, useEffect } from 'react';
import { Modal, Table, Button, Spin, message, Empty } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import _get from 'lodash/get';
import request from 'utils/api';
import TagBillDetail from 'components/TagBillDetail';
import TagTransactionType from 'components/TagTransactionType';
import './styles.less';

const OrderHistoryModal = ({ userId, visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchOrderHistory = async (page = 1, pageSize = 10) => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await request(
        '/api/fonehouse/order-manager/orders',
        {
          userId,
          page,
          limit: pageSize,
        },
        { method: 'GET' }
      );

      const data = _get(response, 'data', {});
      setOrders(_get(data, 'docs', []));
      setPagination({
        current: page,
        pageSize: pageSize,
        total: _get(data, 'totalDocs', 0),
      });
    } catch (error) {
      console.error('Error fetching order history:', error);
      message.error('Không thể tải lịch sử đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && userId) {
      fetchOrderHistory(pagination.current, pagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userId]);

  const handleTableChange = (paginationConfig) => {
    fetchOrderHistory(paginationConfig.current, paginationConfig.pageSize);
  };

  const columns = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 120,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Ngày đặt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (date) => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Sản phẩm',
      dataIndex: 'products',
      key: 'products',
      render: (products) => {
        const productNames = (products || [])
          .map((p) => p.name)
          .filter(Boolean)
          .join(', ');
        return <span className="product-list">{productNames || 'Không có sản phẩm'}</span>;
      },
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      render: (price, record) => (
        <strong className="price-text">
          {(price || 0).toLocaleString()} {record.currency || 'JPY'}
        </strong>
      ),
    },
    {
      title: 'Thanh toán',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 120,
      render: (type) => <TagTransactionType status={type} />,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <TagBillDetail status={status} />,
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          onClick={() => {
            window.open(`/fonehouse/billing_list/${record._id}`, '_blank');
          }}
        >
          Xem chi tiết
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Lịch sử đơn hàng"
      visible={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      className="order-history-modal"
    >
      <Spin spinning={loading}>
        {orders.length === 0 && !loading ? (
          <Empty description="Không có đơn hàng nào" />
        ) : (
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="_id"
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            size="small"
          />
        )}
      </Spin>
    </Modal>
  );
};

OrderHistoryModal.propTypes = {
  userId: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default OrderHistoryModal;
